import React, { useState } from 'react';
import { parseFigmaUrl } from '../utils/figmaUrlParser';
import { FigmaFileResponse } from '../types/annotation';

interface FigmaFileLoaderProps {
  previousFiles: FigmaFileResponse[];
  loading: boolean;
  onLoadFile: (fileKey: string) => void;
  onSelectPreviousFile: (file: FigmaFileResponse) => void;
  onDeleteFile: (fileId: number) => void;
}

const FigmaFileLoader: React.FC<FigmaFileLoaderProps> = ({
  previousFiles,
  loading,
  onLoadFile,
  onSelectPreviousFile,
  onDeleteFile,
}) => {
  const [url, setUrl] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);

  const handleLoad = () => {
    const parsed = parseFigmaUrl(url);
    if (!parsed) {
      setParseError('Invalid Figma URL. Please paste a link like https://www.figma.com/design/ABC123/...');
      return;
    }
    setParseError(null);
    onLoadFile(parsed.fileKey);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLoad();
  };

  return (
    <div style={{ padding: '20px' }}>
      <h3 style={{ margin: '0 0 12px 0' }}>Load Figma File</h3>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <input
          type="text"
          value={url}
          onChange={e => { setUrl(e.target.value); setParseError(null); }}
          onKeyDown={handleKeyDown}
          placeholder="Paste Figma URL here..."
          style={{
            flex: 1,
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
          }}
        />
        <button
          onClick={handleLoad}
          disabled={loading || !url.trim()}
          style={{
            padding: '8px 20px',
            background: loading ? '#ccc' : '#4A90D9',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'default' : 'pointer',
            fontSize: '14px',
          }}
        >
          {loading ? 'Loading...' : 'Load'}
        </button>
      </div>
      {parseError && (
        <div style={{ color: '#e74c3c', fontSize: '13px', marginBottom: '12px' }}>
          {parseError}
        </div>
      )}

      {previousFiles.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>
            Previously Loaded Files
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {previousFiles.map(file => (
              <div
                key={file.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  background: '#f8f9fa',
                  borderRadius: '4px',
                  border: '1px solid #eee',
                }}
              >
                <div
                  style={{ cursor: 'pointer', flex: 1 }}
                  onClick={() => onSelectPreviousFile(file)}
                >
                  <div style={{ fontWeight: 500, fontSize: '14px' }}>
                    {file.fileName || file.fileKey}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {file.annotationCount} annotation{file.annotationCount !== 1 ? 's' : ''}
                  </div>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); onDeleteFile(file.id); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#999',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '4px 8px',
                  }}
                  title="Delete file"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FigmaFileLoader;
